import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { storefront } from "../api/storefront";
import type { Cart } from "../api/types";

const EMPTY_CART: Cart = {
  profileId: "",
  lines: [],
  itemsCount: 0,
  totalKopecks: 0,
};

export const useCartStore = defineStore("cart", () => {
  const cart = ref<Cart>(EMPTY_CART);
  const loading = ref(false);
  const lastError = ref<string | null>(null);

  const itemsCount = computed(() => cart.value.itemsCount);
  const totalKopecks = computed(() => cart.value.totalKopecks);
  const isEmpty = computed(() => cart.value.lines.length === 0);

  function qtyOf(productId: string): number {
    return (
      cart.value.lines.find((l) => l.productId === productId)?.qty ?? 0
    );
  }

  // S1 — a later-issued mutation's response must never be clobbered by an
  // earlier one that happens to resolve afterwards (slow network, out-of-order
  // responses). Every call to apply() gets the next sequence number; a response
  // is only applied if nothing newer has already landed, otherwise it's stale
  // and silently dropped (the fresher state it would've overwritten already
  // reflects — or will reflect once its own response arrives — this one too).
  let mutationSeq = 0;
  let appliedSeq = 0;
  function apply(seq: number, next: Cart): void {
    if (seq < appliedSeq) return;
    appliedSeq = seq;
    cart.value = next;
  }

  async function fetch(): Promise<void> {
    loading.value = true;
    try {
      const seq = ++mutationSeq;
      const res = await storefront.cart.get();
      apply(seq, res.cart);
    } catch (err) {
      lastError.value = (err as Error).message;
    } finally {
      loading.value = false;
    }
  }

  async function addItem(productId: string, qty = 1): Promise<void> {
    lastError.value = null;
    const seq = ++mutationSeq;
    const res = await storefront.cart.addItem(productId, qty);
    apply(seq, res.cart);
  }

  async function setQty(productId: string, qty: number): Promise<void> {
    lastError.value = null;
    const seq = ++mutationSeq;
    const res = await storefront.cart.setItemQty(productId, qty);
    apply(seq, res.cart);
  }

  async function remove(productId: string): Promise<void> {
    lastError.value = null;
    const seq = ++mutationSeq;
    const res = await storefront.cart.removeItem(productId);
    apply(seq, res.cart);
  }

  async function clear(): Promise<void> {
    lastError.value = null;
    const seq = ++mutationSeq;
    const res = await storefront.cart.clear();
    apply(seq, res.cart);
  }

  // S1 (степпер +/-, ProductCard.vue/ProductPage.vue): serialize +/- clicks per
  // product so a second rapid click can never read a stale pre-response qty.
  // Each call is chained after the previous one for the SAME productId (a
  // click while a request is in flight is queued, not dropped and not fired
  // concurrently); the target quantity is (re)computed from the current store
  // state only once it's this call's turn to run, so N queued clicks always
  // add up to a net change of N — never less, and apply() above still guards
  // against this chain racing a direct addItem/setQty/remove/clear call made
  // elsewhere (e.g. CartLineItem.vue) for a different product.
  const stepTail = new Map<string, Promise<void>>();
  const steppingIds = ref<Set<string>>(new Set());
  function isStepping(productId: string): boolean {
    return steppingIds.value.has(productId);
  }
  async function stepNow(productId: string, delta: number): Promise<void> {
    lastError.value = null;
    const target = Math.max(0, qtyOf(productId) + delta);
    const seq = ++mutationSeq;
    const res = target === 0
      ? await storefront.cart.removeItem(productId)
      : await storefront.cart.setItemQty(productId, target);
    apply(seq, res.cart);
  }
  async function step(productId: string, delta: number): Promise<void> {
    steppingIds.value.add(productId);
    const prior = stepTail.get(productId) ?? Promise.resolve();
    const run = prior.catch(() => {}).then(() => stepNow(productId, delta));
    stepTail.set(productId, run);
    try {
      await run;
    } finally {
      if (stepTail.get(productId) === run) {
        stepTail.delete(productId);
        steppingIds.value.delete(productId);
      }
    }
  }

  function reset(): void {
    cart.value = EMPTY_CART;
    loading.value = false;
    lastError.value = null;
  }

  return {
    cart,
    loading,
    lastError,
    itemsCount,
    totalKopecks,
    isEmpty,
    qtyOf,
    fetch,
    addItem,
    setQty,
    remove,
    clear,
    step,
    isStepping,
    reset,
  };
});
